import JSZip from "jszip";
import mime from "mime";
import Papa from "papaparse";
import { database } from "../backend/database";

type CsvObject = {
  category: string;
  section: string;
  item: string;
  task_prefix: string | null;
  task_question: string | null;
  state: string | null;
  recording_count: number;
  photo_count: number;
};

interface props {
  formId: number;
}

function joinPath(...parts: string[]) {
  return parts
    .map((part, i) => {
      if (i === 0) return part.replace(/\/+$/g, ""); // trim trailing slash of first part
      return part.replace(/^\/+|\/+$/g, ""); // trim leading/trailing slashes of middle parts
    })
    .filter(Boolean)
    .join("/");
}

export function sanitizePath(path: string): string {
  return path
    .split(/[\\/]+/) // Split on both / and \ (cross-platform)
    .map(
      (segment) =>
        segment
          // eslint-disable-next-line no-control-regex
          .replace(/[<>:"/\\|?*\x00-\x1F]/g, "") // Remove invalid characters
          .replace(/\s+/g, "_") // Replace spaces with underscores
          .replace(/\.+$/, "") // Remove trailing dots (Windows)
          .slice(0, 255) // Max segment length
    )
    .filter(Boolean) // Remove empty segments
    .join("/"); // Join with safe separator
}

export function removeExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf(".");
  if (dotIndex === -1) return filename; // no extension
  return filename.slice(0, dotIndex);
}

export function getTimestamp(): string {
  const now = new Date();

  const pad = (n: number) => n.toString().padStart(2, "0");

  const day = pad(now.getDate());
  const month = pad(now.getMonth() + 1); // months are 0-indexed
  const year = now.getFullYear();

  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const seconds = pad(now.getSeconds());

  return `${day}${month}${year}_${hours}${minutes}${seconds}`;
}

export const generateZip = async (data: props): Promise<Blob> => {
  const csvData: CsvObject[] = [];

  const zip = new JSZip();

  const form = await database.forms.get(data.formId);

  const [taskOverrides, categories, recordings, photos] = await Promise.all([
    database.taskOverrides.where("formId").equals(form?.id!).toArray(),
    database.categories.where("templateId").equals(form?.templateId!).toArray(),
    database.recordings.where("formId").equals(form?.id!).toArray(),
    database.photos.where("formId").equals(form?.id!).toArray(),
  ]);

  for (const category of categories) {
    const sections = await database.sections
      .where("categoryId")
      .equals(category.id!)
      .toArray();

    for (const section of sections) {
      const items = await database.items
        .where("sectionId")
        .equals(section.id!)
        .toArray();

      for (const item of items) {
        const tasks = await database.tasks
          .where("itemId")
          .equals(item.id!)
          .toArray();

        for (let task of tasks) {
          const override = taskOverrides.find((x) => x.taskId === task.id);

          if (override) {
            task.status = override.status;
          }

          const taskPhotos = photos.filter((x) => x.taskId === task.id);
          const taskRecordings = recordings.filter((x) => x.taskId === task.id);

          const csvObject: CsvObject = {
            category: category.title,
            section: section.title,
            item: item.title,
            task_prefix: task.prefix,
            task_question: task.content,
            state: task.status,
            recording_count: taskRecordings.length,
            photo_count: taskPhotos.length,
          };

          const path = `${category.title}/${section.title}/${item.title}`;
          const fileNamePrefix = `${task.prefix ?? ""}${task.content ?? ""}`;

          for (
            let photoIndex = 0;
            photoIndex < csvObject.photo_count;
            photoIndex++
          ) {
            const photoData = taskPhotos[photoIndex];
            const fileExtension = mime.getExtension(photoData.file.type);

            const photoPath = joinPath(
              path,
              `${fileNamePrefix}_${photoIndex}.${fileExtension}`
            );
            const fullPath = sanitizePath(photoPath);

            zip.file(fullPath, photoData.file);
          }

          for (
            let recordingIndex = 0;
            recordingIndex < csvObject.recording_count;
            recordingIndex++
          ) {
            const recordingData = taskRecordings[recordingIndex];
            const fileExtension = mime.getExtension(recordingData.blob.type);

            const recordingPath = joinPath(
              path,
              `${fileNamePrefix}_${recordingIndex}.${fileExtension}`
            );
            const fullPath = sanitizePath(recordingPath);

            zip.file(fullPath, recordingData.blob);
          }

          csvData.push(csvObject);
        }
      }
    }
  }

  const csv = Papa.unparse(csvData);

  zip.file("result.csv", csv);

  return await zip.generateAsync({ type: "blob" });

  // for (const topicId of data.topicTree) {
  //   const topic = data.topicsById[topicId];

  //   for (const subcategoryId of topic.subcategoryIds) {
  //     const subcategory = data.subcategoriesById[subcategoryId];

  //     for (const requirementId of subcategory.requirementIds) {
  //       const requirement = data.requirementsById[requirementId];

  //       for (const questionId of requirement.questionIds) {
  //         const question = data.questionsById[questionId];

  //         const csvObject: CsvObject = {
  //           topic: topic.title,
  //           subcategory: subcategory.title,
  //           requirement: requirement.title,
  //           question_title: question.title,
  //           question_question: question.question,
  //           state: question.state,
  //           recording_count: question.recordings.length,
  //           photo_count: question.photos.length,
  //         };

  //         csvData.push(csvObject);

  //         const path = `${topic.title}/${subcategory.title}/${requirement.title}`;
  //         const fileNamePrefix = `${question.question ?? ""}${
  //           question.title ?? ""
  //         }`;

  //         for (let index = 0; index < question.recordings.length; index++) {
  //           const recordingData = question.recordings[index];
  //           const fileExtension = mime.getExtension(recordingData.blob.type);

  //           const recordingPath = joinPath(
  //             path,
  //             `${fileNamePrefix}_${index}.${fileExtension}`
  //           );
  //           const fullPath = sanitizePath(recordingPath);

  //           zip.file(fullPath, recordingData.blob);
  //         }

  //         for (let index = 0; index< question.photos.length; index++) {
  //           const photoData = question.photos[index];
  //           const fileExtension = mime.getExtension(photoData.blob.type);

  //           const photoPath = joinPath(
  //             path,
  //             `${fileNamePrefix}_${index}.${fileExtension}`
  //           );
  //           const fullPath = sanitizePath(photoPath);

  //           zip.file(fullPath, photoData.blob);
  //         }
  //       }
  //     }
  //   }
};
